import { logError, logInfo } from './logger'

type PreparedImage = {
  file: File
  dataUrl: string
  width: number
  height: number
}

const DEFAULT_MAX_DIMENSION = 4096
// Netlify function request payload limits can be hit quickly when sending base64 JSON.
// Keep the post-processed binary well below 6MB so base64 + JSON overhead stays safe.
const DEFAULT_MAX_BYTES = 3.75 * 1024 * 1024 // ~3.75MB

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to decode image'))
    }
    img.src = url
  })
}

function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Failed to encode image'))
        resolve(blob)
      },
      mimeType,
      quality
    )
  })
}

function inferOutputMimeType(
  inputMimeType: string
): 'image/jpeg' | 'image/png' {
  return inputMimeType === 'image/png' ? 'image/png' : 'image/jpeg'
}

function getOutputFileName(originalName: string, mimeType: string): string {
  const base = originalName.replace(/\.[^.]+$/, '')
  const ext = mimeType === 'image/png' ? 'png' : 'jpg'
  return `${base}.${ext}`
}

/**
 * Prepare an image for upload to `/.netlify/functions/upload-user-image`.
 *
 * Why: we send a base64 data URL inside JSON. Large images can exceed Netlify request-body limits
 * before our function runs. This helper downscales and (for JPEG) compresses to stay under a safe
 * payload size.
 */
export async function prepareImageForUpload(
  file: File,
  options?: { maxDimension?: number; maxBytes?: number }
): Promise<PreparedImage> {
  const maxDimension = options?.maxDimension ?? DEFAULT_MAX_DIMENSION
  const maxBytes = options?.maxBytes ?? DEFAULT_MAX_BYTES

  const img = await loadImageFromFile(file)
  const srcWidth = img.naturalWidth || img.width
  const srcHeight = img.naturalHeight || img.height

  if (!srcWidth || !srcHeight) {
    throw new Error('Unable to determine image dimensions')
  }

  const scale = Math.min(1, maxDimension / Math.max(srcWidth, srcHeight))
  const targetWidth = Math.max(1, Math.round(srcWidth * scale))
  const targetHeight = Math.max(1, Math.round(srcHeight * scale))

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  // White background prevents unexpected transparency when we later fall back to JPEG.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, targetWidth, targetHeight)
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  // Start by preserving PNG if the user uploaded PNG; otherwise encode to JPEG.
  let outMime = inferOutputMimeType(file.type)
  let quality = 0.9

  let blob =
    outMime === 'image/png'
      ? await canvasToBlob(canvas, outMime)
      : await canvasToBlob(canvas, outMime, quality)

  // If PNG is still too large, fall back to JPEG (common for large screenshots).
  if (blob.size > maxBytes && outMime === 'image/png') {
    outMime = 'image/jpeg'
    quality = 0.9
    blob = await canvasToBlob(canvas, outMime, quality)
  }

  // If JPEG is still too large, iteratively reduce quality.
  while (blob.size > maxBytes && outMime === 'image/jpeg' && quality > 0.6) {
    quality = Math.max(0.6, quality - 0.1)
    blob = await canvasToBlob(canvas, outMime, quality)
  }

  if (blob.size > maxBytes) {
    logError('imageUploadPrep', 'Prepared image still too large', {
      originalBytes: file.size,
      preparedBytes: blob.size,
      outMime,
      width: targetWidth,
      height: targetHeight,
      maxBytes,
    })
    throw new Error(
      'Image is too large to upload. Please choose a smaller image.'
    )
  }

  const outName = getOutputFileName(file.name, outMime)
  const outFile = new File([blob], outName, { type: outMime })
  const dataUrl = await readBlobAsDataUrl(outFile)

  logInfo('imageUploadPrep', 'Prepared image for upload', {
    originalBytes: file.size,
    preparedBytes: outFile.size,
    mimeType: outMime,
    width: targetWidth,
    height: targetHeight,
  })

  return {
    file: outFile,
    dataUrl,
    width: targetWidth,
    height: targetHeight,
  }
}
